import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, MessageSquare, BarChart2, RefreshCcw, Mail, UserCheck, Database, Send, X } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

const features = [
  {
    icon: <Search className="w-6 h-6" />,
    title: "Smart Lead Discovery",
    description: "AI-powered targeting to find your ideal prospects across multiple platforms"
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: "Personalized Icebreakers",
    description: "Auto-generated conversation starters based on prospect data"
  },
  {
    icon: <Database className="w-6 h-6" />,
    title: "Unified Dashboard",
    description: "Manage all your leads and conversations in one central location"
  },
  {
    icon: <BarChart2 className="w-6 h-6" />,
    title: "Analytics & Tracking",
    description: "Monitor campaign performance and engagement metrics"
  }
];

const leads = [
  {
    name: "Sarah Chen",
    title: "VP of Engineering at TechScale",
    email: "sarah.chen@techscale.com",
    icebreaker: "I noticed your recent article on microservices architecture. Would love to discuss how our automation solutions could streamline your deployment pipeline."
  },
  {
    name: "Marcus Rodriguez",
    title: "Head of Operations at InnovateCorp",
    email: "m.rodriguez@innovatecorp.com",
    icebreaker: "Your LinkedIn post about operational challenges in scaling startups caught my attention. Our AI solutions have helped similar companies reduce manual processes by 70%."
  },
  {
    name: "Emily Thompson",
    title: "Digital Transformation Lead at FutureWorks",
    email: "emily.t@futureworks.io",
    icebreaker: "Saw your team is expanding automation initiatives. Would you be interested in learning how our AI agents have helped similar enterprises reduce response times by 85%?"
  }
];

const ProcessStep = ({ number, icon, title, description }) => {
  const { theme } = useTheme();
  
  return (
    <div className="relative flex items-start gap-4">
      <div className={`flex-shrink-0 w-12 h-12 ${
        theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
      } rounded-full flex items-center justify-center`}>
        {icon}
      </div>
      <div>
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-sm font-bold text-white">
          {number}
        </div>
        <h4 className={`text-lg font-semibold mb-2 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>{title}</h4>
        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
          {description}
        </p>
      </div>
    </div>
  );
};

const LeadModal = ({ lead, onClose, type }) => {
  const { theme } = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        } rounded-xl p-6 max-w-lg w-full shadow-xl`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className={`text-xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {type === 'email' ? 'Compose Email' : 'Send Message'}
          </h3>
          <button onClick={onClose} className={`p-1 rounded ${
            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
          }`}>
            <X className={`w-5 h-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`} />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
            } rounded-full flex items-center justify-center`}>
              <UserCheck className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className={`font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{lead.name}</h4>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {lead.title}
              </p>
            </div>
          </div>

          {type === 'email' && (
            <div className={`${
              theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
            } p-3 rounded mb-4`}>
              <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                To: {lead.email}
              </p>
            </div>
          )}

          <div className={`${
            theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
          } p-4 rounded`}>
            <p className={`text-sm mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>Suggested message:</p>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              {lead.icebreaker}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 transition-colors text-white flex items-center gap-2"
          >
            {type === 'email' ? (
              <>
                <Mail className="w-4 h-4" />
                Send Email
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Dashboard = () => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalType, setModalType] = useState(null);
  const { theme } = useTheme();

  const openModal = (lead, type) => {
    setSelectedLead(lead);
    setModalType(type);
  };

  return (
    <>
      <div className={`${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      } rounded-xl p-6 shadow-xl`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Lead Dashboard</h3>
          <RefreshCcw className="w-5 h-5 text-blue-400" />
        </div>
        
        <div className="space-y-4">
          {leads.map((lead, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.2 }}
              className={`${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
              } p-4 rounded-lg`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${
                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                  } rounded-full flex items-center justify-center`}>
                    <UserCheck className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>{lead.name}</h4>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                      {lead.title}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => openModal(lead, 'email')}
                    className={`p-2 rounded-lg transition-colors ${
                      theme === 'dark'
                        ? 'hover:bg-gray-600'
                        : 'hover:bg-gray-200'
                    }`}
                  >
                    <Mail className="w-4 h-4 text-blue-400" />
                  </button>
                  <button 
                    onClick={() => openModal(lead, 'message')}
                    className={`p-2 rounded-lg transition-colors ${
                      theme === 'dark'
                        ? 'hover:bg-gray-600'
                        : 'hover:bg-gray-200'
                    }`}
                  >
                    <Send className="w-4 h-4 text-blue-400" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-400">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: i * 0.3, duration: 1 }}
                  className={`h-1 ${
                    theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
                  } rounded-full mt-2`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedLead && (
          <LeadModal
            lead={selectedLead}
            type={modalType}
            onClose={() => {
              setSelectedLead(null);
              setModalType(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default function LeadGenerationSection() {
  const { theme } = useTheme();
  
  return (
    <section id="lead-generation-section" className={`py-20 px-4 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Users className="w-12 h-12 text-blue-500" />
          <h2 className={`text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Lead Generation & Outreach</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="space-y-8">
            <div className="prose prose-invert max-w-none">
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Transform your outreach with our AI-powered lead generation platform. Automatically discover and engage with qualified prospects using personalized messaging at scale.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-lg'
                  } p-6 rounded-xl`}
                >
                  <div className="text-blue-500 mb-4">{feature.icon}</div>
                  <h3 className={`text-xl font-semibold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>{feature.title}</h3>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-24">
            <Dashboard />
          </div>
        </div>

        <div className={`${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-lg'
        } rounded-xl p-8`}>
          <h3 className={`text-2xl font-bold mb-8 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ProcessStep
              number={1}
              icon={<Search className="w-6 h-6 text-blue-400" />}
              title="Lead Discovery"
              description="Our AI scans multiple platforms to identify potential leads matching your ideal customer profile."
            />
            <ProcessStep
              number={2}
              icon={<Database className="w-6 h-6 text-blue-400" />}
              title="Data Enrichment"
              description="We automatically gather and verify contact information and relevant details about each lead."
            />
            <ProcessStep
              number={3}
              icon={<MessageSquare className="w-6 h-6 text-blue-400" />}
              title="Personalized Outreach"
              description="AI generates customized icebreakers based on each prospect's background and interests."
            />
            <ProcessStep
              number={4}
              icon={<BarChart2 className="w-6 h-6 text-blue-400" />}
              title="Engagement Tracking"
              description="Monitor responses and engagement metrics to optimize your outreach strategy."
            />
          </div>
        </div>
      </div>
    </section>
  );
}